import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import {
  House, ChartLineUp, MapTrifold, MagnifyingGlass, PenNib, Eye, ChatCircleText,
  FileText, Buildings, Gear, SignOut, CaretDown, PaperPlaneTilt, Robot,
  Users, Phone, Storefront, Lightning, Funnel, Camera, Crosshair, BookOpenText, CheckCircle,
} from "@phosphor-icons/react";
import RankTrustLogo from "@/components/RankTrustLogo";

const navBusiness = [
  { to: "/app", label: "Dashboard", icon: House, end: true },
  { to: "/app/playbook", label: "Playbook", icon: BookOpenText },
  { to: "/app/leads", label: "Pipeline", icon: Users },
  { to: "/app/attribution", label: "Attribution", icon: Funnel },
  { to: "/app/snapshots", label: "Snapshots", icon: Camera },
  { to: "/app/rank-tracker", label: "Rank Tracker", icon: ChartLineUp },
  { to: "/app/geo-grid", label: "Geo-Grid", icon: MapTrifold },
  { to: "/app/chatgpt-visibility", label: "AI Search Visibility", icon: Robot },
  { to: "/app/audit", label: "AI Audit", icon: MagnifyingGlass },
  { to: "/app/content", label: "Content Studio", icon: PenNib },
  ...(process.env.REACT_APP_CONTENT_ENGINE_ENABLED === "true" ? [{ to: "/app/content-engine", label: "Content Engine", icon: FileText }] : []),
  { to: "/app/landing-pages", label: "Landing Pages", icon: Storefront },
  { to: "/app/growth", label: "Growth Tools", icon: Lightning },
  { to: "/app/voice-agent", label: "Voice Agent", icon: Phone },
  { to: "/app/competitors", label: "Competitor Spy", icon: Eye },
  { to: "/app/trust-profile", label: "RankTrust Profile", icon: Storefront },
  { to: "/app/reviews", label: "Reviews", icon: ChatCircleText },
  { to: "/app/review-moderation", label: "Review Moderation", icon: CheckCircle },
  { to: "/app/review-requests", label: "Review Requests", icon: PaperPlaneTilt },
  { to: "/app/reports", label: "Reports", icon: FileText },
];

const navExtra = [
  { to: "/app/prospecting", label: "Prospecting", icon: Crosshair, agencyOnly: true },
  { to: "/app/agency", label: "Agency Hub", icon: Buildings, agencyOnly: true },
  { to: "/app/settings", label: "Settings", icon: Gear },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [businesses, setBusinesses] = useState([]);
  const [activeBiz, setActiveBiz] = useState(null);
  const [showBizDropdown, setShowBizDropdown] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/businesses");
        setBusinesses(data);
        if (data.length) {
          const stored = localStorage.getItem("active_business_id");
          const found = stored && data.find((b) => b.id === stored);
          setActiveBiz(found || data[0]);
        }
      } catch {}
    })();
  }, []);

  useEffect(() => {
    if (activeBiz) localStorage.setItem("active_business_id", activeBiz.id);
  }, [activeBiz]);

  const onLogout = async () => {
    await logout();
    nav("/");
  };

  const allNav = [...navBusiness, ...navExtra.filter((n) => !n.agencyOnly || user?.role === "agency")];

  return (
    <div className="min-h-screen flex bg-zinc-50">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-zinc-200 hidden md:flex flex-col" data-testid="sidebar">
        <div className="p-5 border-b border-zinc-200">
          <RankTrustLogo height={30} asLink tagline />
          <div className="mt-2 leading-snug" data-testid="dashboard-tagline">
            <div className="text-[11px] font-bold tracking-wider uppercase text-zinc-700">AI Acquisition Platform</div>
            <div className="text-[10px] text-zinc-500 italic mt-0.5">Find, analyze, and acquire high-opportunity Google Maps businesses.</div>
          </div>
          {user?.agency_brand?.brand_name && (
            <div className="mt-2 label-tiny">Powered by {user.agency_brand.brand_name}</div>
          )}
        </div>

        {/* Business switcher */}
        <div className="p-3 border-b border-zinc-200 relative">
          <button
            onClick={() => setShowBizDropdown(!showBizDropdown)}
            className="w-full flex items-center justify-between gap-2 p-3 border border-zinc-200 hover:border-zinc-400 transition-colors"
            data-testid="business-switcher"
          >
            <div className="text-left overflow-hidden">
              <div className="label-tiny">Active Business</div>
              <div className="text-sm font-semibold truncate">{activeBiz?.name || "No business yet"}</div>
            </div>
            <CaretDown size={14} weight="bold" />
          </button>
          {showBizDropdown && (
            <div className="absolute left-3 right-3 mt-1 bg-white border border-zinc-200 z-20 max-h-64 overflow-auto" data-testid="business-list">
              {businesses.map((b) => (
                <button
                  key={b.id}
                  onClick={() => { setActiveBiz(b); setShowBizDropdown(false); }}
                  className="w-full text-left p-3 hover:bg-zinc-50 border-b border-zinc-100 last:border-b-0 text-sm"
                  data-testid={`switch-biz-${b.id}`}
                >
                  <div className="font-semibold">{b.name}</div>
                  <div className="text-xs text-zinc-500">{b.city}, {b.state}</div>
                </button>
              ))}
              <Link to="/onboarding" onClick={()=>setShowBizDropdown(false)} className="block w-full text-left p-3 text-sm font-semibold text-[#002FA7] bg-zinc-50" data-testid="add-business">
                + Add a business
              </Link>
            </div>
          )}
        </div>

        <nav className="flex-1 py-3">
          {allNav.map(({ to, label, icon: Icon, end }) => {
            const active = end ? loc.pathname === to : loc.pathname.startsWith(to);
            return (
              <Link key={to} to={to} className={`nav-item ${active ? "active" : ""}`} data-testid={`nav-${label.toLowerCase().replace(/ /g, "-")}`}>
                <Icon size={16} weight="regular" />
                <span>{label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-zinc-200 p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm overflow-hidden">
              <div className="font-semibold truncate">{user?.name}</div>
              <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
            </div>
            <button onClick={onLogout} className="p-2 text-zinc-500 hover:text-zinc-900" data-testid="logout-btn"><SignOut size={16} /></button>
          </div>
          {user?.plan_status === "trial" && (
            <Link to="/pricing" className="btn-primary w-full justify-center mt-3 !py-2 !text-[11px]" data-testid="upgrade-btn">Upgrade</Link>
          )}
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-auto">
          <Outlet context={{ activeBiz, setActiveBiz, businesses, setBusinesses }} />
        </div>
      </main>
    </div>
  );
}
